import { format } from 'date-fns';
import { isEmpty, last, nth, orderBy } from 'lodash';

import { ChartDatum } from '@/types/charts';

export const getLastXMonths = ({ data, months }: { data: ChartDatum[]; months: number }): ChartDatum[] => {
  return orderBy(data.slice(0, months), ['date']);
};

export const NUMBER_VALUE_FORMATS = {
  FRACTION: 'fraction',
  WHOLE_NUMBER: 'wholeNumber',
} as const;

type DataType = (typeof NUMBER_VALUE_FORMATS)[keyof typeof NUMBER_VALUE_FORMATS];

interface GetLastTwoMonthsDataSummary<T> {
  data: T[];
  key: keyof T;
  dataType?: DataType;
  decimalPlaces?: number;
}

/**
 * Get the last and second to last months' values of a given key
 * and calculate the difference between them.
 */
export const getLastTwoMonthsDataSummary = <T extends ChartDatum>({
  data,
  key,
  dataType = NUMBER_VALUE_FORMATS.FRACTION,
  decimalPlaces = 2,
}: GetLastTwoMonthsDataSummary<T>): {
  lastValue: string;
  secondToLastValue: string;
  difference: string;
  hasIncreased: boolean;
  latestMonthText: string;
} => {
  if (isEmpty(data)) {
    return { lastValue: '0', secondToLastValue: '0', difference: '0', hasIncreased: false, latestMonthText: '' };
  }

  const dataOrderedByDate = orderBy(data, ['date']);
  const latestMonth: T | undefined = last(dataOrderedByDate);
  const theMonthBeforeTheLatestMonth: T | undefined = nth(dataOrderedByDate, -2);

  const lastValueRaw = Number(latestMonth?.[key]) || 0;
  const secondToLastValueRaw = Number(theMonthBeforeTheLatestMonth?.[key]) || 0;
  const multiplier = dataType === NUMBER_VALUE_FORMATS.FRACTION ? 100 : 1;

  const lastValue = lastValueRaw * multiplier;
  const secondToLastValue = secondToLastValueRaw * multiplier;

  const difference = lastValue - secondToLastValue;
  const hasIncreased = lastValue > secondToLastValue;

  return {
    lastValue: lastValue.toFixed(decimalPlaces),
    secondToLastValue: secondToLastValue.toFixed(decimalPlaces),
    difference: difference.toFixed(decimalPlaces),
    hasIncreased,
    latestMonthText: format(new Date(`${latestMonth?.date}T00:00:00`), 'MMM'),
  };
};

interface FormatNumbersForChartOptions {
  data?: any[];
  key: string;
  multiplier?: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export const formatNumbersForChart = ({
  data = [],
  key,
  multiplier = 1,
  minimumFractionDigits = 1,
  maximumFractionDigits = 1,
}: FormatNumbersForChartOptions) => {
  return data.map((datum) => ({
    date: datum.date,
    [key]: Number(datum[key] * multiplier).toLocaleString(undefined, {
      minimumFractionDigits,
      maximumFractionDigits,
    }),
  }));
};
